/* =========================================================================
   GROUNDED DROPS — MOTION ENGINE  ("First Light" package, Jul 2026)
   Pairs with gd-motion.css. Progressive enhancement only:
   - No JS  -> html.gd-motion never exists -> nothing is ever hidden.
   - Reduced motion -> engine no-ops entirely; server-rendered content stands.
   - This Horizon theme scrolls a custom container, NOT window; everything
     here is IntersectionObserver-driven, which is immune to that.
   - After each reveal completes, data-gd-reveal is stripped so elements
     return to pristine theme styling (hover transforms, transitions).
     The .is-inview class stays behind for follow-on choreography.
   - Idempotent; re-runs on shopify:section:load for theme-editor safety.
   ========================================================================= */
(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  var STEP_DEFAULT = 60;        // ms between items arriving in the same batch
  var DUR = 360;                // matches --gd-dur-slow
  var CLEANUP_SLACK = 900;      // covers pip/chip/badge choreography tails

  var observer = null;
  var batchQueue = [];
  var batchScheduled = false;

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    // Any intersection at all -> reveal instantly. This must be at least as
    // generous as the observer's rootMargin so no element can fail this
    // check yet fire the observer immediately (a visible blip).
    return r.top < vh && r.bottom > 0;
  }

  function stepFor(el) {
    var p = el.closest('[data-gd-reveal-stagger]');
    if (p) {
      var v = parseInt(p.getAttribute('data-gd-reveal-stagger'), 10);
      if (!isNaN(v) && v >= 0) return v;
    }
    return STEP_DEFAULT;
  }

  function finish(el, delay) {
    window.setTimeout(function () {
      el.removeAttribute('data-gd-reveal');
      el.style.removeProperty('--gd-reveal-d');
    }, delay + DUR + CLEANUP_SLACK);
  }

  function revealNow(el) {
    el.classList.add('is-inview');
    el.removeAttribute('data-gd-reveal');
    el.style.removeProperty('--gd-reveal-d');
  }

  /* Entries that intersect in the same frame form one batch and stagger
     against each other; a lone card scrolled into view gets zero delay. */
  function flushBatch() {
    batchScheduled = false;
    var batch = batchQueue.splice(0);
    var i = 0;
    batch.forEach(function (el) {
      if (!el.isConnected || el.classList.contains('is-inview')) return;
      var delay = i * stepFor(el);
      i += 1;
      el.style.setProperty('--gd-reveal-d', delay + 'ms');
      el.classList.add('is-inview');
      finish(el, delay);
    });
  }

  function onIntersect(entries) {
    var queued = false;
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      batchQueue.push(entry.target);
      queued = true;
    });
    if (queued && !batchScheduled) {
      batchScheduled = true;
      window.requestAnimationFrame(flushBatch);
    }
  }

  /* ---- Review count-up (count only; the rating renders instantly) ------ */
  function countUp(agg) {
    if (agg.hasAttribute('data-gd-counted')) return;
    agg.setAttribute('data-gd-counted', '');
    var strongs = agg.querySelectorAll('.gd-reviews__agg-text strong');
    if (strongs.length < 2) { agg.classList.add('is-inview'); return; }
    var node = strongs[1];
    var finalText = node.textContent;
    var target = parseInt(finalText.replace(/[^0-9]/g, ''), 10);
    if (!target || target <= 0) { agg.classList.add('is-inview'); return; }

    // Screen readers get the real value once; the ~55 intermediate text
    // mutations are hidden from the accessibility tree for the duration.
    node.setAttribute('aria-hidden', 'true');
    var real = document.createElement('span');
    real.className = 'gd-visually-hidden';
    real.textContent = finalText;
    node.insertAdjacentElement('afterend', real);

    agg.classList.add('is-inview'); // triggers the star cascade in CSS
    var t0 = null;
    var DURATION = 900;
    function frame(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min((ts - t0) / DURATION, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      node.textContent = Math.round(target * eased).toLocaleString('en-AU');
      if (p < 1) {
        window.requestAnimationFrame(frame);
      } else {
        node.textContent = finalText; // restore the exact server string
        node.removeAttribute('aria-hidden');
        real.remove();
      }
    }
    window.requestAnimationFrame(frame);
  }

  function watchAggregate(scope) {
    var aggs = (scope || document).querySelectorAll('.gd-reviews__agg:not([data-gd-counted])');
    aggs.forEach(function (agg) {
      if (inViewport(agg)) { countUp(agg); return; }
      new IntersectionObserver(function (entries, io) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          countUp(entry.target);
        });
      }, { threshold: 0.4 }).observe(agg);
    });
  }

  /* Back/forward restores arrive mid-page (scrollRestoration is forced to
     'auto' in theme.liquid) — replaying the whole cascade there feels
     broken, so reveal everything instantly on those navigations. */
  function isBackForward() {
    try {
      var nav = performance.getEntriesByType('navigation');
      return !!(nav && nav[0] && nav[0].type === 'back_forward');
    } catch (e) { return false; }
  }

  /* ---- Init / re-init (idempotent) ------------------------------------- */
  function init(scope) {
    var root = scope || document;
    var instantAll = !scope && isBackForward();

    // Expand groups: children inherit the reveal attribute.
    root.querySelectorAll('[data-gd-reveal-group]').forEach(function (group) {
      Array.prototype.forEach.call(group.children, function (child) {
        if (!child.hasAttribute('data-gd-reveal') && !child.classList.contains('is-inview')) {
          child.setAttribute('data-gd-reveal', '');
        }
      });
    });

    var targets = [];
    root.querySelectorAll('[data-gd-reveal]').forEach(function (el) {
      if (el.classList.contains('is-inview')) { el.removeAttribute('data-gd-reveal'); return; }
      // Anything already on screen shows instantly — decided in the same
      // synchronous pass that gates hiding, so it can never flicker.
      if (instantAll || inViewport(el)) { revealNow(el); return; }
      targets.push(el);
    });

    // Gate CSS only once initial visibility is resolved.
    document.documentElement.classList.add('gd-motion');

    if (targets.length) {
      if (!observer) {
        observer = new IntersectionObserver(onIntersect, {
          threshold: 0.15,
          rootMargin: '0px 0px -4% 0px'
        });
      }
      targets.forEach(function (el) { observer.observe(el); });
    }

    watchAggregate(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });

  // Keyboard safety: focus landing inside a not-yet-revealed element (e.g.
  // tabbing to a format card below the fold) reveals it immediately so
  // focus is never on an invisible link.
  document.addEventListener('focusin', function (e) {
    if (!e.target || !e.target.closest) return;
    var el = e.target.closest('[data-gd-reveal]');
    if (el) {
      if (observer) observer.unobserve(el);
      revealNow(el);
    }
  });
})();
